import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Redirect, Route, useHistory, useLocation } from "react-router-dom";
import { isSystemUser } from "@/utils/orgTypeUtils";

import commonClient from "@/apis/commonClient";
import userClient from "@/apis/userClient";
import * as actions from "@/redux/actions";
import { isMenuRootEntry } from "@/types/NavigationState";
import { useOrg } from "@/contexts/OrgContext";

import Cookies from "js-cookie";
import authenticationClient from "@/apis/authenticationClient";
import Loading from "@/components/ReactBootstrap/LoaderSpinner/Loading";
import { setLocale } from "@/constants/SensolusMoment";
import RouteAccessGuard from "@/containers/AdminLayout/RouteAccessGuard";
import { useCustomBranding } from "@/pages/CustomBranding/useCustomBranding";
import {
  WebSocketProvider,
  useWebSocket,
} from "@/contexts/WebSocketContext";
import { adminRoutes } from "@/containers/App/routes";
import Header from "./Header/Header";
import HelpSidebar from "./HelpSidebar/HelpSidebar";
import Sidebar from "./Sidebar/Sidebar";
import TermOfUse from "./TermOfUse/TermOfUse";
import ImpersonateHeader from "./Header/ImpersonateHeader";
import useDemoEnvironment from "@/hooks/useDemoEnvironment";

import SensolusWhileLogoIcon from "@images/sensolus/sensolus_logo_white.svg";
import SensolusLogoIcon from "@images/sensolus/sensolus_logo.svg";
import businessIntelligenceClient from "@/apis/businessIntelligenceClient";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import SntDialog from "@/components/SntDialog/SntDialog";
import NotificationSidebar from "@/cleanup/containers/NotificationSidebar/NotificationSidebar";
import FavoritesSidebar from "@/containers/PageHeader/FavoritesSidebar";
import AiChatSidebar from "@/cleanup/containers/AiChatSidebar/AiChatSidebar";
import { useAppLanguage } from "@/contexts/AppLanguageContext";
import { RootState, DashboardNotification } from "@/types/RootState";
import type { RouteConfig } from "@/types/RouteConfig";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";

interface AppLayoutState {
  loading: boolean;
  redirect: boolean;
}

interface WebSocketContextValue {
  isConnected: boolean;
  connectionStatus: string;
  lastMessage: unknown;
  sessionId: string | null;
  connect: (jwtToken?: string, csrfToken?: string) => void;
  disconnect: () => void;
  sendMessage: (message: unknown) => void;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
}

const AppLayoutContent: React.FC = () => {
  const [state, setState] = useState<AppLayoutState>({
    loading: true,
    redirect: false,
  });

  const setCookie = useCookies(["_csrf"])[1];
  const sensolusFullScreen = Cookies.get("sensolus-full-screen");
  const dispatch = useDispatch();

  // R8: the demo deployment always shows the environment bar, so the layout must be
  // offset for it exactly as it is while impersonating.
  const isDemoEnvironment = useDemoEnvironment();
  const loginInfo = useSelector((state: RootState) => state.user, shallowEqual);
  const history = useHistory();
  const location = useLocation();
  const [reloadCount, setReloadCount] = useState<number>(0);
  const { scopeOrgId, isAllOrgs } = useOrg();

  /**
   * Remount the whole page when the browsed organisation changes.
   *
   * Owning this here is the point: 16 pages had each re-implemented it in their
   * own dep array, no two the same — five of them forgot the org entirely, and
   * two keyed on the raw url parameter, which is `null` at own level AND at All
   * and so never fired on the transition between them.
   *
   * The key is the LEVEL's organisation, not the url's. At All it is `"all"`
   * even on a detail page carrying `?org=42`, because All is what the selector
   * chose and the org merely names the open record. That is what keeps
   * `updateOrg()` — which every detail page calls once loaded, to correct the
   * url to its record's owner — from reading as an org change and remounting
   * the page it has only just mounted.
   */
  const orgKey = isAllOrgs ? "all" : String(scopeOrgId ?? "");
  const [isShowTermOfUse, setIsShowTermOfUse] = useState<boolean>(false);
  const [impersonateUser, setImpersonateUser] = useState<Record<string, unknown> | undefined>();
  
  // WebSocket integration
  const { connect, disconnect, isConnected, connectionStatus } = useWebSocket() as WebSocketContextValue;

  const appLanguageContext = useAppLanguage();
  const { appLanguage, setAppLanguage } = (appLanguageContext || {}) as { appLanguage?: string; setAppLanguage?: (lang: string) => void };

  const { menuType, dashboardNotification, preventSidebar } = useSelector(
    (state: RootState) => state.all,
    shallowEqual
  );

  const { getHomeLogoImgSrc, getAdminLogoImgSrc } = useCustomBranding();

  useEffect(() => {
    // The variant follows the menu, for everyone who has a logo slot: the
    // branded sidebar is coloured at Home and pale at admin, and both a normal
    // organisation and a partner draw their logo there (Sidebar.tsx). System
    // never reads this — Header.tsx uses its own `sensolus_logo_header.svg`.
    //
    // This was briefly keyed on `isPartnerUser` instead, while a partner's logo
    // sat on the light header: the Home variant is a white knockout and it
    // disappeared there. Moving the logo back to the sidebar removed the reason.
    const useHomeVariant = menuType === "MAIN";

    let src: string | null = null;
    if (loginInfo && loginInfo.custumInfo) {
      src = useHomeVariant
        ? getHomeLogoImgSrc(loginInfo.custumInfo)
        : getAdminLogoImgSrc(loginInfo.custumInfo);
    }
    if (!src) {
      src = useHomeVariant ? SensolusWhileLogoIcon : SensolusLogoIcon;
    }
    dispatch(actions.setLogoPath(src));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo, menuType]);

  useEffect(() => {
    if (loginInfo) {
      // A NORMAL login used to get `?org=<ownId>` planted here, because its
      // pages needed an org in the URL. Since STIC-15615 the absence of the
      // param *is* own level — which is the only level such a login has — so
      // planting it would push it onto the cross level and hide the own-level
      // menu items. Pages are unaffected: they read `orgId || own org id`.
      setImpersonateUser(loginInfo.impersonateData);
    }
  }, [loginInfo]);

  const reloadPage = useCallback(() => {
    setReloadCount((prevReload) => prevReload + 1);
  }, []);

  useEffect(() => {
    if (appLanguage) {
      document.documentElement.lang = appLanguage === "en-gb" ? "en" : appLanguage;
    }
  }, [appLanguage]);

  useEffect(() => {
    const csrfName = sessionStorage.getItem("csrf_name");
    const csrf = csrfName ? Cookies.get(csrfName) : undefined;

    authenticationClient
      .getUserInfo()
      .then((data) => {
        data.data.csrfToken = csrf;
        const loginData = data.data;

        const previousLocale = sessionStorage.getItem("SNT-LOCALE");
        dispatch(actions.loginSuccess(loginData));
        const languageKey = loginData.language;
        if (setAppLanguage) {
          setAppLanguage(languageKey);
        }

        const onLanguageReady = () => {
          setLocale(languageKey, loginData.timezone);
          setState({ loading: false, redirect: false });

          connect(loginData.token, csrf);

          commonClient
            .getMessageOnUserPage()
            .then((data) => {
              dispatch(actions.updateMessage(data.data));
            })
            .catch(() => {});

          if (loginData.termsOfUseInfo && !loginData.termsOfUseInfo.ok) {
            setIsShowTermOfUse(true);
          }
        };

        if (previousLocale === languageKey) {
          onLanguageReady();
        } else {
          userClient.getLanguage(languageKey).then((data) => {
            dispatch(actions.updateLanguage(data.data));
            onLanguageReady();
          });
        }
      })
      .catch(() => {
        setState({ loading: false, redirect: true });
      });

    // Track the in-app history depth exactly by mirroring the browser
    // stack with location keys (STIC-15588). historyCount = number of
    // in-app entries behind the current one; it gates the header back
    // arrow. A plain +1 counter goes wrong on REPLACE (tab/mode toggles)
    // and on browser back/forward (POP), which this handles natively.
    const keyOf = (loc: { key?: string }) => loc.key || "initial";
    const visitedKeys: string[] = [keyOf(history.location)];
    let pointer = 0;
    const listen = history.listen((location, action) => {
      const key = keyOf(location);
      if (action === "PUSH") {
        if (isMenuRootEntry(location)) {
          // Sidebar-menu navigation starts a fresh root: top-level pages
          // never show the back arrow; drill-ins count from here.
          visitedKeys.length = 0;
          visitedKeys.push(key);
          pointer = 0;
        } else {
          visitedKeys.length = pointer + 1; // drop forward entries
          visitedKeys.push(key);
          pointer++;
        }
      } else if (action === "REPLACE") {
        visitedKeys[pointer] = key;
      } else if (action === "POP") {
        const idx = visitedKeys.indexOf(key);
        if (idx !== -1) {
          pointer = idx;
        } else {
          // Landed on an entry we never saw (e.g. navigated past our
          // record) — treat it as a fresh start with no in-app history.
          visitedKeys.length = 0;
          visitedKeys.push(key);
          pointer = 0;
        }
      }
      dispatch(actions.setHistoryCount(pointer));
    });

    return () => {
      listen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCookie, dispatch, history]);

  useEffect(() => {}, [isConnected, connectionStatus]);

  useEffect(() => {
    return () => {
      console.log("AppLayout unmounting, disconnecting WebSocket...");
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.loading) {
    return null;
  }

  if (state.redirect) {
    if (
      location.pathname &&
      location.pathname.length > 0 &&
      location.pathname !== "/tracked_assets" &&
      location.pathname !== "/"
    ) {
      return (
        <Redirect
          to={
            "/login" +
            (location.search && location.search.length > 0
              ? location.search + "&"
              : "?") +
            "redirect_to=" +
            location.pathname
          }
        />
      );
    }
    return <Redirect to={"/login"} />;
  }

  const getRoute = (obj: RouteConfig, key: number): React.ReactElement => {
    let component = obj;
    if (obj.path === "/tracked_assets" && isSystemUser(loginInfo)) {
      // SYSTEM users have no Home menu, so the /tracked_assets list leaves the sidebar
      // mismatched/stuck. Redirect to the equivalent system page (/trackers). Centralized
      // here so every entry point to /tracked_assets (logo, "view assets" actions, deep
      // links, bookmarks) is covered in one place (STIC-15147).
      // Carry both parameters across the redirect: `org` is the organisation,
      // `scope=all` the level, and dropping either changes where the sidebar sits.
      const carried = new URLSearchParams();
      const current = new URLSearchParams(location.search);
      for (const key of ["org", "scope"]) {
        const value = current.get(key);
        if (value) carried.set(key, value);
      }
      const orgSearch = carried.toString();
      component = {
        ...obj,
        // Keep the ?org= context on the redirect so the sidebar stays on the
        // selected org (STIC-15687); other tracked_assets params don't apply.
        component: () => (
          <Redirect
            to={{ pathname: "/trackers", search: orgSearch ? `?${orgSearch}` : "" }}
          />
        ),
      };
    } else if (
      !(
        hasPermission(UserPermission.DEVICE_METADATA_VIEW) ||
        hasPermission(UserPermission.DEVICE_UPDATE)
      ) &&
      obj.path === "/tracked_assets"
    ) {
      component = {
        ...obj,
        component: () => {
          return <></>;
        },
      };
    }

    return (
      <Route
        key={key}
        path={obj.path}
        exact={obj.exact}
        render={() => {
          const content = React.cloneElement(
            component.component() as React.ReactElement<{ reload?: number }>,
            {
              reload: reloadCount,
            }
          );
          return (
            <Suspense fallback={<Loading />}>
              <div key={`${reloadCount}:${orgKey}`}>
                {/* STIC-15133: route-level access condition → /no_access */}
                {obj.access ? (
                  <RouteAccessGuard condition={obj.access}>{content}</RouteAccessGuard>
                ) : (
                  content
                )}
              </div>
            </Suspense>
          );
        }}
      />
    );
  };

  const listMenu = adminRoutes.map((obj, key) => {
    return getRoute(obj, key);
  });

  const saveDashboard = (data: DashboardNotification): void => {
    const descriptor = JSON.stringify({ widgets: data.widgets });
    businessIntelligenceClient
      .update(String(data.id), { descriptor: descriptor, dashboardType: "PLATFORM" })
      .then(() => {
        dispatch(actions.setDashboardNotification(null));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div
      className={`wrapper  ${sensolusFullScreen && "full-screen-mode"} ${
        impersonateUser || isDemoEnvironment ? "impersonate-mode" : ""
      }`}
    >
      {!preventSidebar && !sensolusFullScreen ? (
        <>
          <ImpersonateHeader
            impersonateUser={impersonateUser}
            isDemoEnvironment={isDemoEnvironment}
            onLeaveClicked={() => {
              setImpersonateUser(undefined);
            }}
          />

          <Header reloadPage={reloadPage} />
          <Sidebar />
        </>
      ) : (
        <></>
      )}

      {!preventSidebar ? (
        <div className="main-panel snt-main-panel">
          <div className="content">
            <div className="container-fluid">{listMenu}</div>
          </div>
        </div>
      ) : (
        <></>
      )}

      <FavoritesSidebar />
      <HelpSidebar orgId={loginInfo.organisation?.id} />
      <NotificationSidebar />
      <AiChatSidebar />

      <TermOfUse
        isShow={isShowTermOfUse}
        setIsShow={setIsShowTermOfUse}
        loginInfo={loginInfo}
      />

      {dashboardNotification && (
        <SntDialog
          isShow={true}
          onSave={() => saveDashboard(dashboardNotification)}
          onClose={() => {
            dispatch(actions.setDashboardNotification(null));
          }}
          title={"Update the changed dashboard settings"}
          saveTxt={translate(LanguageKey.save_key)}
          closeTxt={translate(LanguageKey.cancel_key)}
          backdrop={"static"}
        >
          {() => {
            return (
              <div>
                Do you want to save the changes made to the settings of{" "}
                <b>{dashboardNotification.title}</b> dashboard?
              </div>
            );
          }}
        </SntDialog>
      )}
    </div>
  );
};

const AppLayout: React.FC = () => {
  return (
    <WebSocketProvider>
      <AppLayoutContent />
    </WebSocketProvider>
  );
};

export default AppLayout;
